import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Search, Trash2, ChevronRight, ChevronDown, LayoutList, Table2 } from 'lucide-react';
import { useProjectStore } from '../../../store/projectStore';
import { useAuthStore } from '../../../store/authStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { useUsersStore } from '../../../store/usersStore';
import { useInventoryStore } from '../../../store/inventoryStore';
import {
  useStockRequestStore,
  StockRequest,
  StockRequestLine,
  fmtRequestDate,
  fmtRequestDay,
  formatRequestRef,
  effectiveLineItemId,
} from '../../../store/stockRequestStore';
import { useStockUiStore } from '../../../store/stockUiStore';
import {
  getRequestTheme,
  statusBadge,
  fmtRequestStatusLabel,
  fmtStatusLabel,
  requestStatusBadgeClass,
  supplierBadgeClass,
  supplierBadgeStyle,
} from './theme';
import UserBadge from './UserBadge';
import { isInReviewQueue, isInReceivingQueue } from '../../../store/stockRequestAssignment';
import { useStockRequestSettingsStore } from '../../../store/stockRequestSettingsStore';
import NewRequestWizard from './NewRequestWizard';
import RequestDetailModal from './RequestDetailModal';
import RequestSheetTable from './RequestSheetTable';

export type RequestsSubTab = 'my_requests' | 'to_review' | 'receiving';

const SUB_TABS: { key: RequestsSubTab; label: string; description: string }[] = [
  { key: 'my_requests', label: 'My Requests', description: 'Requests you have submitted' },
  { key: 'to_review', label: 'To Review', description: 'Submitted requests awaiting approval or ordering' },
  { key: 'receiving', label: 'Receiving', description: 'Ordered stock ready to receive' },
];

/** Fixed table layout — aligned with Inventory stock table */
const BASE_TABLE_COLUMNS = [
  { key: 'request', label: 'Request', width: 100 },
  { key: 'status', label: 'Status', width: 116 },
  { key: 'destination', label: 'Destination', width: '18%' },
  { key: 'requester', label: 'Requester', width: '12%' },
  { key: 'assignedTo', label: 'Assigned To', width: '12%' },
  { key: 'submitted', label: 'Submitted', width: 132 },
  { key: 'updated', label: 'Last updated', width: 132 },
  { key: 'action', label: '', width: 60 },
] as const;

const tableColumnsForTab = (subTab: RequestsSubTab) => {
  if (subTab === 'to_review') return BASE_TABLE_COLUMNS;
  return BASE_TABLE_COLUMNS.filter(col => col.key !== 'assignedTo' && col.key !== 'requester');
};

const cellTruncate = 'overflow-hidden text-ellipsis whitespace-nowrap max-w-0';

/** TODO: gate behind manager permission */
const canDeleteRequests = true;

function lineItemLabel(
  line: StockRequestLine,
  itemNameById: Map<string, string>,
): string {
  if (line.lineType === 'free_text' && !line.itemId && !line.substituteItemId) {
    return line.freeTextDescription ?? '—';
  }
  const id = effectiveLineItemId(line);
  if (id && itemNameById.has(id)) return itemNameById.get(id)!;
  if (line.itemId && itemNameById.has(line.itemId)) return itemNameById.get(line.itemId)!;
  return line.freeTextDescription ?? '—';
}

const RequestsView: React.FC = () => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const theme = getRequestTheme(isDark);
  const { user } = useAuthStore();
  const { warehouses, storageAreasByWarehouse } = useWarehouseStore();
  const { users, fetchUsers } = useUsersStore();
  const { items, fetchItems } = useInventoryStore();
  const { requests, isLoading, fetchRequests, deleteRequest, error: requestError } = useStockRequestStore();
  const { fetchSettings } = useStockRequestSettingsStore();
  const { requestsViewMode, setRequestsViewMode } = useStockUiStore();

  const [subTab, setSubTab] = useState<RequestsSubTab>('my_requests');
  const [search, setSearch] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [hoveredTab, setHoveredTab] = useState<RequestsSubTab | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const isSheetMode = requestsViewMode === 'by_item';

  const openWizard = useCallback(() => setIsWizardOpen(true), []);

  const handleRequestUpdated = useCallback(() => {
    void fetchRequests();
  }, [fetchRequests]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchUsers();
    fetchSettings();
    fetchItems();
  }, [fetchRequests, fetchUsers, fetchSettings, fetchItems]);

  useEffect(() => {
    setExpandedIds(new Set());
  }, [subTab]);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const { requestId } = (event as CustomEvent<{ requestId: string }>).detail ?? {};
      if (!requestId) return;
      void fetchRequests().then(() => {
        const r = useStockRequestStore.getState().requests.find(x => x.id === requestId);
        if (r) {
          if (isInReceivingQueue(r.status)) setSubTab('receiving');
          else if (isInReviewQueue(r.status, r.assignmentStep)) setSubTab('to_review');
          else setSubTab('my_requests');
        }
        setSelectedRequestId(requestId);
      });
    };
    window.addEventListener('openStockRequest', onOpen);
    return () => window.removeEventListener('openStockRequest', onOpen);
  }, [fetchRequests]);

  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) map.set(u.id, u.displayName);
    return map;
  }, [users]);

  const itemNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) map.set(item.id, item.name);
    return map;
  }, [items]);

  const whName = (id: string) => warehouses.find(w => w.id === id)?.name ?? '—';
  const areaName = (whId: string, areaId: string | null) => {
    if (!areaId) return null;
    return (storageAreasByWarehouse[whId] ?? []).find(a => a.id === areaId)?.name ?? null;
  };

  const filtered = useMemo(() => {
    let list = requests;

    if (subTab === 'my_requests') {
      list = list.filter(r => r.requesterId === user?.id);
      if (!showCompleted) {
        list = list.filter(r => r.status !== 'completed');
      }
    } else if (subTab === 'to_review') {
      list = list.filter(r => isInReviewQueue(r.status, r.assignmentStep));
    } else {
      list = list.filter(r => isInReceivingQueue(r.status));
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(r => {
        const ref = formatRequestRef(r.requestNumber).toLowerCase();
        const wh = whName(r.warehouseId).toLowerCase();
        return ref.includes(q) || wh.includes(q) || r.status.includes(q);
      });
    }

    return list;
  }, [requests, subTab, user?.id, search, warehouses, showCompleted]);

  const selectedRequest = useMemo(
    () => (selectedRequestId ? requests.find(r => r.id === selectedRequestId) ?? null : null),
    [requests, selectedRequestId],
  );

  const myRequestCount = useMemo(
    () => requests.filter(r => r.requesterId === user?.id).length,
    [requests, user?.id],
  );

  const reviewCount = useMemo(
    () => requests.filter(r => isInReviewQueue(r.status, r.assignmentStep)).length,
    [requests],
  );

  const receivingCount = useMemo(
    () => requests.filter(r => isInReceivingQueue(r.status)).length,
    [requests],
  );

  const badgeForTab = (key: RequestsSubTab) => {
    if (key === 'to_review') return reviewCount;
    if (key === 'receiving') return receivingCount;
    return 0;
  };

  const handleDeleteRequest = async (id: string) => {
    setDeleteError(null);
    const ok = await deleteRequest(id);
    if (ok) {
      setConfirmDeleteId(null);
      setSelectedRequestId(prev => (prev === id ? null : prev));
      setExpandedIds(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } else {
      setDeleteError(useStockRequestStore.getState().error ?? 'Failed to delete request');
    }
  };

  const tableColumns = tableColumnsForTab(subTab);

  const modalSubTab = useMemo((): RequestsSubTab => {
    if (!isSheetMode || !selectedRequest) return subTab;
    if (isInReceivingQueue(selectedRequest.status)) return 'receiving';
    if (isInReviewQueue(selectedRequest.status, selectedRequest.assignmentStep)) return 'to_review';
    return 'my_requests';
  }, [isSheetMode, selectedRequest, subTab]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative" style={{ backgroundColor: theme.pageBg }}>

      {/* Sub-tab bar — By request only */}
      {!isSheetMode && (
        <div
          className="flex items-center gap-1 px-4 pt-2 pb-0 flex-shrink-0"
          style={{ backgroundColor: theme.barBg, borderBottom: `1px solid ${theme.border}` }}
        >
          {SUB_TABS.map(tab => {
            const isActive = subTab === tab.key;
            const count = badgeForTab(tab.key);
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSubTab(tab.key)}
                onMouseEnter={() => setHoveredTab(tab.key)}
                onMouseLeave={() => setHoveredTab(null)}
                title={hoveredTab === tab.key && !isActive ? tab.description : undefined}
                className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-t-md transition-colors"
                style={{
                  backgroundColor: isActive ? theme.activeTabBg : theme.inactiveTabBg,
                  color: isActive ? theme.activeTabTxt : theme.inactiveTabTxt,
                  marginBottom: '-1px',
                  border: '1px solid transparent',
                  borderTopColor: isActive ? theme.border : 'transparent',
                  borderLeftColor: isActive ? theme.border : 'transparent',
                  borderRightColor: isActive ? theme.border : 'transparent',
                  borderBottomColor: isActive ? theme.activeTabBg : 'transparent',
                }}
              >
                {tab.label}
                {count > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-none"
                    style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Toolbar — By request only; By item places these next to status chips */}
      {!isSheetMode && (
        <div
          className="flex items-center gap-2 px-4 py-2 flex-shrink-0"
          style={{ borderBottom: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}
        >
          <div className="relative flex-1 max-w-xs">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: theme.muted }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search request #, warehouse…"
              className="w-full h-7 text-xs pl-7 pr-3 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
            />
          </div>
          {subTab === 'my_requests' && (
            <label className="flex items-center gap-1.5 text-sm cursor-pointer flex-shrink-0" style={{ color: theme.text }}>
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={e => setShowCompleted(e.target.checked)}
              />
              Show Completed
            </label>
          )}

          <div
            className="inline-flex items-center rounded border overflow-hidden flex-shrink-0"
            style={{ borderColor: theme.border }}
            title="How to show stock requests"
          >
            <button
              type="button"
              onClick={() => setRequestsViewMode('by_item')}
              className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium transition-colors"
              style={{
                backgroundColor: 'transparent',
                color: theme.muted,
              }}
            >
              <Table2 size={12} />
              By item
            </button>
            <button
              type="button"
              onClick={() => setRequestsViewMode('by_request')}
              className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium transition-colors"
              style={{
                backgroundColor: isDark ? '#1d2535' : '#eff6ff',
                color: '#60a5fa',
                borderLeft: `1px solid ${theme.border}`,
              }}
            >
              <LayoutList size={12} />
              By request
            </button>
          </div>

          <button
            type="button"
            onClick={openWizard}
            className="inline-flex items-center gap-1.5 h-7 px-2.5 ml-auto bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors text-xs font-medium"
          >
            <Plus size={13} />
            New Request
          </button>
          {(deleteError || requestError) && (
            <p className="text-xs text-red-400 ml-2">{deleteError || requestError}</p>
          )}
        </div>
      )}

      {isSheetMode && (deleteError || requestError) && (
        <p className="text-xs text-red-400 px-4 pt-2 flex-shrink-0">{deleteError || requestError}</p>
      )}

      {isSheetMode ? (
        <RequestSheetTable
          search={search}
          onSearchChange={setSearch}
          isDark={isDark}
          onNewRequest={openWizard}
        />
      ) : (
      /* Table — layout matches Inventory */
      <div className="flex-1 min-h-0 px-4 pt-3 pb-4 flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto rounded" style={{ border: `1px solid ${theme.border}` }}>
          <table className="w-full table-fixed">
            <colgroup>
              {tableColumns.map(col => (
                <col key={col.key} style={{ width: typeof col.width === 'number' ? `${col.width}px` : col.width }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-10" style={{ backgroundColor: theme.headerBg }}>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                {tableColumns.map(col => (
                  <th
                    key={col.key}
                    className="px-3 py-2 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap"
                    style={{ color: theme.muted }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody style={{ backgroundColor: theme.cardBg }}>
              {isLoading && filtered.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-3 py-10 text-center text-sm" style={{ color: theme.muted }}>
                    Loading requests…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-3 py-10 text-center text-sm" style={{ color: theme.muted }}>
                    <div className="flex flex-col items-center gap-2">
                      <span>
                        {subTab === 'my_requests'
                          ? (myRequestCount === 0
                            ? 'No requests yet.'
                            : showCompleted
                              ? 'No requests match your search.'
                              : 'No active requests.')
                          : 'Nothing in this queue.'}
                      </span>
                      {subTab === 'my_requests' && (
                        <button
                          type="button"
                          onClick={openWizard}
                          className="text-xs text-blue-500 hover:text-blue-400"
                        >
                          Create your first request →
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((req, idx) => (
                  <RequestRow
                    key={req.id}
                    req={req}
                    theme={theme}
                    isDark={isDark}
                    isLast={idx === filtered.length - 1}
                    colSpan={tableColumns.length}
                    isExpanded={expandedIds.has(req.id)}
                    itemNameById={itemNameById}
                    canDelete={canDeleteRequests}
                    isConfirmDelete={confirmDeleteId === req.id}
                    showRequester={subTab === 'to_review'}
                    showAssignedTo={subTab === 'to_review'}
                    requesterId={req.requesterId}
                    requesterName={userNameById.get(req.requesterId) ?? 'Unknown'}
                    assignedToId={req.assignedTo}
                    assignedToName={req.assignedTo ? (userNameById.get(req.assignedTo) ?? 'Unknown') : '—'}
                    destination={`${whName(req.warehouseId)}${areaName(req.warehouseId, req.storageAreaId) ? ` / ${areaName(req.warehouseId, req.storageAreaId)}` : ''}`}
                    onToggleExpand={() => toggleExpanded(req.id)}
                    onOpen={() => setSelectedRequestId(req.id)}
                    onDeleteClick={() => setConfirmDeleteId(req.id)}
                    onDeleteConfirm={() => handleDeleteRequest(req.id)}
                    onDeleteCancel={() => setConfirmDeleteId(null)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {isWizardOpen && (
        <NewRequestWizard
          onClose={() => setIsWizardOpen(false)}
          onCreated={(id) => {
            setIsWizardOpen(false);
            setSelectedRequestId(id);
            fetchRequests();
          }}
          onOpenRequest={(id) => setSelectedRequestId(id)}
        />
      )}

      {selectedRequestId && selectedRequest && (
        <RequestDetailModal
          requestId={selectedRequestId}
          subTab={modalSubTab}
          initialRequest={selectedRequest}
          destination={`${whName(selectedRequest.warehouseId)}${areaName(selectedRequest.warehouseId, selectedRequest.storageAreaId) ? ` / ${areaName(selectedRequest.warehouseId, selectedRequest.storageAreaId)}` : ''}`}
          requesterName={userNameById.get(selectedRequest.requesterId) ?? 'Unknown'}
          onClose={() => setSelectedRequestId(null)}
          onRequestUpdated={handleRequestUpdated}
        />
      )}

    </div>
  );
};

const RequestRow: React.FC<{
  req: StockRequest;
  theme: ReturnType<typeof getRequestTheme>;
  isDark: boolean;
  isLast: boolean;
  colSpan: number;
  isExpanded: boolean;
  itemNameById: Map<string, string>;
  canDelete: boolean;
  isConfirmDelete: boolean;
  showRequester: boolean;
  showAssignedTo: boolean;
  requesterId: string;
  requesterName: string;
  assignedToId: string | null;
  assignedToName: string;
  destination: string;
  onToggleExpand: () => void;
  onOpen: () => void;
  onDeleteClick: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}> = ({
  req,
  theme,
  isDark,
  isLast,
  colSpan,
  isExpanded,
  itemNameById,
  canDelete,
  isConfirmDelete,
  showRequester,
  showAssignedTo,
  requesterId,
  requesterName,
  assignedToId,
  assignedToName,
  destination,
  onToggleExpand,
  onOpen,
  onDeleteClick,
  onDeleteConfirm,
  onDeleteCancel,
}) => {
  const lines = useMemo(
    () => [...(req.lines ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [req.lines],
  );

  return (
    <React.Fragment>
      <tr
        className="group cursor-pointer transition-colors"
        style={{ borderBottom: isLast && !isExpanded ? undefined : `1px solid ${theme.border}` }}
        onClick={onOpen}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
      >
        <td className={`px-3 py-1.5 text-sm font-medium ${cellTruncate}`}>
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onToggleExpand();
              }}
              className="flex-shrink-0 inline-flex items-center justify-center rounded transition-colors"
              style={{ color: theme.muted, padding: 4, margin: -4 }}
              onMouseEnter={e => {
                e.currentTarget.style.color = theme.text;
                e.currentTarget.style.backgroundColor = isDark
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgba(0,0,0,0.08)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = theme.muted;
                e.currentTarget.style.backgroundColor = '';
              }}
              title={isExpanded ? 'Collapse items' : 'Expand items'}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse items' : 'Expand items'}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            <span className="text-blue-400 truncate">{formatRequestRef(req.requestNumber)}</span>
          </span>
        </td>
        <td className="px-3 py-1.5">
          <span className={`${requestStatusBadgeClass} ${statusBadge(req.status, isDark)}`}>
            {fmtRequestStatusLabel(req.status)}
          </span>
        </td>
        <td className={`px-3 py-1.5 text-sm ${cellTruncate}`} style={{ color: theme.text }}>{destination}</td>
        {showRequester && (
          <td className={`px-3 py-1.5 ${cellTruncate}`}>
            <UserBadge userId={requesterId} name={requesterName} isDark={isDark} />
          </td>
        )}
        {showAssignedTo && (
          <td className={`px-3 py-1.5 ${cellTruncate}`}>
            {assignedToId ? (
              <UserBadge userId={assignedToId} name={assignedToName} isDark={isDark} />
            ) : (
              <span className="text-sm" style={{ color: theme.muted }}>—</span>
            )}
          </td>
        )}
        <td className="px-3 py-1.5 text-sm whitespace-nowrap" style={{ color: theme.muted }}>
          {fmtRequestDate(req.submittedAt ?? req.createdAt)}
        </td>
        <td className="px-3 py-1.5 text-sm whitespace-nowrap" style={{ color: theme.muted }}>
          {fmtRequestDate(req.updatedAt)}
        </td>
        <td className="px-3 py-1.5 text-right" onClick={e => e.stopPropagation()}>
          {canDelete && (
            isConfirmDelete ? (
              <span className="inline-flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={onDeleteConfirm}
                  className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors"
                >
                  Delete
                </button>
                <span style={{ color: theme.muted }}>·</span>
                <button
                  type="button"
                  onClick={onDeleteCancel}
                  className="text-xs transition-colors"
                  style={{ color: theme.muted }}
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={onDeleteClick}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-900/30"
                style={{ color: theme.muted }}
                title="Delete request"
              >
                <Trash2 size={13} />
              </button>
            )
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr style={{ borderBottom: isLast ? undefined : `1px solid ${theme.border}` }}>
          <td colSpan={colSpan} className="p-0" style={{ backgroundColor: theme.subTableBg }}>
            {lines.length === 0 ? (
              <p className="px-3 py-1.5 pl-9 text-sm" style={{ color: theme.muted }}>No line items</p>
            ) : (
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: 72 }} />
                  <col style={{ width: 140 }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: 132 }} />
                  <col style={{ width: 80 }} />
                </colgroup>
                <thead style={{ backgroundColor: theme.subTableHeaderBg }}>
                  <tr>
                    {(
                      [
                        { key: 'item', label: 'Item', className: 'pl-9 pr-3' },
                        { key: 'qty', label: 'Qty', className: 'px-3' },
                        { key: 'status', label: 'Status', className: 'px-3' },
                        { key: 'po', label: 'PO Number', className: 'px-3' },
                        { key: 'supplier', label: 'Supplier', className: 'px-3' },
                        { key: 'eta', label: 'Expected Delivery', className: 'px-3' },
                        { key: 'received', label: 'Received', className: 'px-3' },
                      ] as const
                    ).map(col => (
                      <th
                        key={col.key}
                        className={`${col.className} py-2 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap`}
                        style={{ color: theme.muted }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map(line => {
                    const label = lineItemLabel(line, itemNameById);
                    const qty = line.approvedQty ?? line.requestedQty;
                    const showQty = line.status !== 'cancelled' && line.status !== 'unavailable';
                    return (
                      <tr
                        key={line.id}
                        style={{ borderTop: `1px solid ${theme.border}` }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.subTableRowHover)}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                      >
                        <td className="pl-9 pr-3 py-1.5 text-sm" style={{ color: theme.text }}>
                          <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
                            <span className="truncate">{label}</span>
                            {line.substituteItemId && line.substituteItemId !== line.itemId && (
                              <span className={`${requestStatusBadgeClass} ${statusBadge('substitute', isDark)}`}>
                                Sub
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-sm whitespace-nowrap tabular-nums" style={{ color: theme.text }}>
                          {showQty ? qty : ''}
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`${requestStatusBadgeClass} ${statusBadge(line.status, isDark)}`}>
                            {fmtStatusLabel(line.status)}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-sm truncate" style={{ color: theme.text }} title={line.poReference ?? undefined}>
                          {line.poReference ?? ''}
                        </td>
                        <td className="px-3 py-1.5 text-sm min-w-0">
                          {line.supplier ? (
                            <span
                              className={`${supplierBadgeClass} align-middle`}
                              style={supplierBadgeStyle(line.supplier, isDark)}
                              title={line.supplier}
                            >
                              {line.supplier}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-1.5 text-sm whitespace-nowrap" style={{ color: theme.text }}>
                          {line.expectedDate ? fmtRequestDay(line.expectedDate) : ''}
                        </td>
                        <td className="px-3 py-1.5 text-sm whitespace-nowrap tabular-nums" style={{ color: theme.text }}>
                          {line.receivedQty > 0 || ['ordered', 'partially_received', 'received', 'completed'].includes(line.status)
                            ? line.receivedQty
                            : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default RequestsView;
